/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { Button, Flex, Link, Text, Box } from '@chakra-ui/react';
import DeleteIcon from '@/assets/svg/delete-bg.svg?react';
import WarningIcon from '@/assets/svg/delete-bg.svg?react';
import EyeIcon from '@/assets/svg/eye.svg?react';
import AddEditReviewModal from './AddEditReviewModal'; // Use AddEditReviewModal
import CustomConfirmationModal from '../common/ConfirmationModal';
import useCustomToast from '../common/Toast';
import NormalDatatable from '../common/NormalDatatable';
import Rating from '../common/Rating';
import ViewReviewModal from './ViewReviewModal';
import {
  useGetAdminReviews,
  useGetAdminReview,
  useCreateAdminReview,
  usePatchAdminReview,
  useDeleteAdminReview,
} from '@/services/reviews';
import { Column } from 'react-table';
import { type AdminReview } from '@/services/reviews/types';

interface Vendor {
  id: number;
  label: string;
}

interface ActionMenuItem {
  label: string;
  icon?: React.ComponentType;
  onClick: (rowId: string) => void;
  isNegative?: boolean;
}

// Mock date formatter
const formatApiDateToDisplay = (date: string): string => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const vendors: Vendor[] = [
  { id: 1, label: 'Vendor A' },
  { id: 2, label: 'Vendor B' },
];

const ratingOptions = Array.from({ length: 5 }, (_, i) => {
  const rating = (i + 1).toString();
  return { id: rating, label: `${rating}.0 Stars` };
});

const AdminReview = () => {
  const { showToast } = useCustomToast();
  const [search, setSearch] = useState<string>('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [viewId, setViewId] = useState<number | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [testimonialToDelete, setTestimonialToDelete] = useState<number | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 10,
    totalCount: 0,
    totalPages: 1,
  });

  // Fetch testimonials
  const {
    data: testimonialsData,
    isLoading: isTestimonialsLoading,
    refetch,
  } = useGetAdminReviews({
    search,
    page: pagination.page,
    page_size: pagination.pageSize,
  });

  // Fetch single testimonial for editing
  const { data: editTestimonial, isLoading: isEditTestimonialLoading } = useGetAdminReview(
    editId ?? 0,
    {
      enabled: !!editId,
    },
  );

  // Mutations
  const { mutate: createTestimonial, isLoading: isCreating } = useCreateAdminReview();
  const { mutate: updateTestimonial, isLoading: isUpdating } = usePatchAdminReview();
  const { mutate: deleteTestimonial, isLoading: isDeleting } = useDeleteAdminReview();

  // Update pagination
  useEffect(() => {
    if (testimonialsData) {
      setPagination((prev) => ({
        ...prev,
        totalCount: testimonialsData.count || testimonialsData.results.length,
        totalPages: Math.max(
          1,
          Math.ceil((testimonialsData.count || testimonialsData.results.length) / prev.pageSize),
        ),
      }));
    }
  }, [testimonialsData]);

  const handlePageChange = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  const handleOpenModal = useCallback(() => {
    setEditId(null);
    setIsModalOpen(true);
  }, []);

  // const handleEdit = useCallback((rowId: string) => {
  //   const id = Number(rowId);
  //   setEditId(id);
  //   setIsModalOpen(true);
  // }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditId(null);
  }, []);

  const handleDelete = useCallback((rowId: string) => {
    const id = parseInt(rowId);
    setTestimonialToDelete(id);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setTestimonialToDelete(null);
  }, []);

  const handleSubmit = useCallback(
    (payload: { rating: string; reviewer_name: string; content: string }) => {
      setIsSubmitting(true);
      const formData = new FormData();
      formData.append('rating', payload.rating || '0');
      formData.append('reviewer_name', payload.reviewer_name || '');
      formData.append('content', payload.content || '');

      if (editId !== null) {
        updateTestimonial(
          { id: editId, formData },
          {
            onSuccess: () => {
              showToast('success', 'Testimonial updated successfully.');
              refetch();
              handleCloseModal();
            },
            onError: (error) => {
              showToast('error', error.message || 'Failed to update testimonial.');
            },
            onSettled: () => {
              setIsSubmitting(false);
            },
          },
        );
      } else {
        if (testimonialsData?.results.length && testimonialsData.results.length >= 1) {
          showToast('error', 'Cannot add more than one testimonial.');
          setIsSubmitting(false);
          return;
        }
        createTestimonial(formData, {
          onSuccess: () => {
            showToast('success', 'Testimonial created successfully.');
            refetch();
            handleCloseModal();
          },
          onError: (error) => {
            showToast('error', error.message || 'Failed to create testimonial.');
          },
          onSettled: () => {
            setIsSubmitting(false);
          },
        });
      }
    },
    [
      editId,
      testimonialsData,
      showToast,
      refetch,
      handleCloseModal,
      createTestimonial,
      updateTestimonial,
    ],
  );

  const confirmDeleteTestimonial = useCallback(() => {
    if (testimonialToDelete !== null) {
      deleteTestimonial(testimonialToDelete, {
        onSuccess: () => {
          showToast('success', 'Testimonial deleted successfully.');
          refetch();
          handleCloseDeleteModal();
          if (testimonialsData?.results.length === 1 && pagination.page > 1) {
            setPagination((prev) => ({ ...prev, page: prev.page - 1 }));
          }
        },
        onError: (error) => {
          showToast('error', error.message || 'Failed to delete testimonial.');
        },
      });
    }
  }, [
    testimonialToDelete,
    showToast,
    refetch,
    handleCloseDeleteModal,
    testimonialsData,
    pagination.page,
    deleteTestimonial,
  ]);

  const handleViewDetails = useCallback((rowId: string) => {
    const id = Number(rowId);
    setViewId(id);
    setIsViewModalOpen(true);
  }, []);

  const handleCloseViewModal = useCallback(() => {
    setIsViewModalOpen(false);
    setViewId(null);
  }, []);

  const filteredData = useMemo(() => {
    return testimonialsData?.results || [];
  }, [testimonialsData]);

  const columns: Column<AdminReview>[] = [
    {
      Header: 'Reviewer Name',
      accessor: 'reviewer_name',
      width: 200,
      minWidth: 200,
      maxWidth: 200,
      Cell: ({ value }: { value: string }) => (
        <Box minW="150px" maxW="200px" overflow="hidden">
          <Text whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
            {value || '-'}
          </Text>
        </Box>
      ),
    },
    {
      Header: 'Rating',
      accessor: 'rating',
      Cell: ({ value }: { value: number }) => (
        <Box overflow="hidden">
          <Flex align="center" gap={1}>
            <Rating initialRating={value} boxSize="5" readOnly={true} />
          </Flex>
        </Box>
      ),
    },
    {
      Header: 'Comment Preview',
      accessor: 'content',
      width: 300,
      minWidth: 300,
      maxWidth: 300,
      Cell: ({ value, row }: { value: string; row: { original: AdminReview } }) => (
        <Box minW="200px" maxW="300px" overflow="hidden">
          <Flex align="center" gap={2}>
            <Text whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
              {value.length > 100 ? `${value.slice(0, 100)}...` : value}{' '}
              {value.length > 100 && (
                <Link
                  color="brand.primary.DEFAULT"
                  textDecoration="underline"
                  fontWeight="medium"
                  onClick={() => handleViewDetails(row.original.id.toString())}
                  display="inline"
                  whiteSpace="nowrap"
                >
                  read more
                </Link>
              )}
            </Text>
          </Flex>
        </Box>
      ),
    },
    {
      Header: 'Created At',
      accessor: 'created_at',
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      Cell: ({ value }: { value: string }) => (
        <Box minW="150px" maxW="150px" overflow="hidden">
          <Text whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
            {formatApiDateToDisplay(value)}
          </Text>
        </Box>
      ),
    },
    {
      Header: 'Updated At',
      accessor: 'updated_at',
      width: 150,
      minWidth: 150,
      maxWidth: 150,
      Cell: ({ value }: { value: string }) => (
        <Box minW="150px" maxW="150px" overflow="hidden">
          <Text whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
            {formatApiDateToDisplay(value)}
          </Text>
        </Box>
      ),
    },
  ];

  const actionMenuItems: ActionMenuItem[] = [
    {
      label: 'View Details',
      icon: EyeIcon,
      onClick: handleViewDetails,
    },
    // {
    //   label: 'Edit',
    //   icon: EditIcon,
    //   onClick: handleEdit,
    // },
    {
      label: 'Delete',
      icon: DeleteIcon,
      onClick: handleDelete,
      isNegative: true,
    },
  ];

  return (
    <>
      <NormalDatatable
        data={filteredData}
        columns={columns}
        onSearch={setSearch}
        pagination={pagination}
        onPageChange={handlePageChange}
        headerButton={
          filteredData.length !== 1 ? (
            <Button
              bg="brand.primary.DEFAULT"
              color="white"
              _hover={{ bg: 'brand.primary.600' }}
              onClick={handleOpenModal}
            >
              + Add New Testimonial
            </Button>
          ) : null
        }
        isLoading={isTestimonialsLoading}
        showSearch={false}
        actionMenuItems={actionMenuItems}
        rowIdAccessor="id"
      />
      <AddEditReviewModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        editId={editId}
        editTestimonial={
          editTestimonial
            ? {
                rating: editTestimonial.rating,
                reviewer_name: editTestimonial.reviewer_name,
                content: editTestimonial.content,
              }
            : undefined
        }
        isEditTestimonialLoading={isEditTestimonialLoading}
        vendors={vendors}
        ratingOptions={ratingOptions}
        onSubmit={handleSubmit}
        isLoading={isSubmitting || isCreating || isUpdating}
      />
      <CustomConfirmationModal
        isOpen={isDeleteModalOpen}
        isLoading={isDeleting}
        onClose={handleCloseDeleteModal}
        onConfirm={confirmDeleteTestimonial}
        icon={WarningIcon}
        title="Delete Review?"
        subtitle="Are you sure you want to delete this review?"
        description=" This action cannot be undone."
        buttonText="Yes, Delete"
      />
      <ViewReviewModal
        isOpen={isViewModalOpen}
        onClose={handleCloseViewModal}
        selectedTestimonial={testimonialsData?.results.find((t) => t.id === viewId) ?? null}
      />
    </>
  );
};

export default AdminReview;
